import React, { useEffect, useRef, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Search from '../../assets/images/svg/search.svg';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';

const inputStyle = {
    height: '46px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
};

const labelStyle = {
    color: '#4A5568',
    fontWeight: '500',
    fontSize: '16px',
};

const cardStyle = {
    border: '1px solid #E2E8F0',
    backgroundColor: '#FAFAFA',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
};

const badgeStyle = {
    minWidth: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#6366F1',
    color: 'white',
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
};

// Default (unconfigured) per-aarti config — never persisted until the admin
// actually sets something; see buildConfigValue below.
const DEFAULT_ENTRY_CONFIG = {
    aarti_audio_artifact_id: null,
    icon_playback_mode: 'NOT_APPLICABLE',
    icon_playback_from: '',
    icon_playback_to: '',
};

const getEntryConfig = (value, aartiId) =>
    value.find((v) => v.aarti_id === aartiId) || { aarti_id: aartiId, ...DEFAULT_ENTRY_CONFIG };

/**
 * Per-Temple Aarti schedule configuration for a Live Darshan.
 *
 * Fetches the tagged Temple's Aarti timings (read-only — gpx_route.
 * teertha_aarti_schedules is never written here) and lets the admin map an
 * Audio Artifact + an Icon Playback Time window to each one. Emits the result
 * as `aarti_config`, shaped exactly as the backend's parseAartiConfig expects.
 */
const AartiSchedulePicker = ({ wptId, audioArtifacts, artifactsFetched, onFetchArtifacts, value, onChange }) => {
    const [scheduleEntries, setScheduleEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchByAartiId, setSearchByAartiId] = useState({});
    const [sameAsFirstByAartiId, setSameAsFirstByAartiId] = useState({});
    // Only clear the parent's config on a genuine re-tag — going from one real
    // temple to a *different* real temple. Arriving at a temple from "none yet"
    // must NOT clear: that covers both a fresh first tag (Create) and Edit's
    // hydration catching up a render after mount (wptId starts null, then is
    // set from the loaded stream) — neither should wipe an already-saved config.
    const prevWptIdRef = useRef(null);

    useEffect(() => {
        const prevWptId = prevWptIdRef.current;
        prevWptIdRef.current = wptId;
        if (prevWptId && wptId && prevWptId !== wptId) {
            onChange([]);
        }

        if (!wptId) {
            setScheduleEntries([]);
            setError(null);
            return;
        }
        // Piggyback on the same shared audio-artifact fetch AudioPlaylistPicker
        // uses, so the search field here has data even if the admin never
        // touches the regular playlist field.
        if (!artifactsFetched && onFetchArtifacts) onFetchArtifacts();
        let cancelled = false;
        setLoading(true);
        setError(null);
        _axios.get(`${ServerPath}/live-darshan/waypoints/${wptId}/aarti-schedule`)
            .then((res) => {
                if (cancelled) return;
                if (res.data.success === 1) {
                    setScheduleEntries(res.data.data?.aarti || []);
                } else {
                    setError('Could not load this temple\'s Aarti schedule.');
                }
            })
            .catch(() => { if (!cancelled) setError('Could not load this temple\'s Aarti schedule.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [wptId]);

    const updateEntry = (aartiId, patch) => {
        const current = getEntryConfig(value, aartiId);
        const updated = { ...current, ...patch };
        onChange([...value.filter((v) => v.aarti_id !== aartiId), updated]);
    };

    if (!wptId) {
        return (
            <div className='mb-3' style={{ color: '#A0AEC0', fontSize: '14px' }}>
                Tag a Temple/Teertha above to configure its Aarti schedule.
            </div>
        );
    }

    if (loading) {
        return <div className='mb-3' style={{ color: '#A0AEC0', fontSize: '14px' }}>Loading Aarti schedule...</div>;
    }

    if (error) {
        return <div className='mb-3 text-danger' style={{ fontSize: '14px' }}>{error}</div>;
    }

    if (scheduleEntries.length === 0) {
        return (
            <div className='mb-3' style={{ color: '#A0AEC0', fontSize: '14px' }}>
                This Temple has no Aarti schedule configured.
            </div>
        );
    }

    const firstAartiId = scheduleEntries[0].aarti_id;

    return (
        <Form.Group className='mb-3'>
            <Form.Label className='mb-2' style={labelStyle}>Temple Aarti Timings</Form.Label>
            {scheduleEntries.map((entry, idx) => {
                const config = getEntryConfig(value, entry.aarti_id);
                const search = searchByAartiId[entry.aarti_id] || '';
                const isFirst = entry.aarti_id === firstAartiId;
                const sameAsFirst = !!sameAsFirstByAartiId[entry.aarti_id];
                const selectedArtifact = config.aarti_audio_artifact_id
                    ? audioArtifacts.find((a) => a.artifact_id === config.aarti_audio_artifact_id)
                    : null;

                const filteredResults = audioArtifacts.filter(
                    (a) => (a.title || '').toLowerCase().includes(search.toLowerCase())
                );

                return (
                    <div key={entry.aarti_id} style={cardStyle}>
                        <Row className='align-items-start'>
                            <Col md={4} className='d-flex align-items-start' style={{ gap: '10px' }}>
                                <span style={badgeStyle}>{idx + 1}</span>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#2D3748' }}>{entry.aarti_type}</div>
                                    <div style={{ fontSize: '13px', color: '#718096' }}>
                                        {entry.start_time} To {entry.end_time}
                                    </div>
                                </div>
                            </Col>

                            <Col md={5}>
                                <Form.Label className='mb-2' style={labelStyle}>
                                    Aarti Audio Artifact <span className='text-danger'>*</span>
                                </Form.Label>
                                {!isFirst && (
                                    <Form.Check
                                        type='checkbox'
                                        id={`same-as-first-${entry.aarti_id}`}
                                        className='mb-2'
                                        label={`Same as ${scheduleEntries[0].aarti_type}`}
                                        checked={sameAsFirst}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setSameAsFirstByAartiId((prev) => ({ ...prev, [entry.aarti_id]: checked }));
                                            if (checked) {
                                                const firstConfig = getEntryConfig(value, firstAartiId);
                                                updateEntry(entry.aarti_id, {
                                                    aarti_audio_artifact_id: firstConfig.aarti_audio_artifact_id,
                                                });
                                            }
                                        }}
                                    />
                                )}
                                {!sameAsFirst && (
                                    <div className='position-relative'>
                                        {selectedArtifact ? (
                                            <div
                                                className='d-flex align-items-center justify-content-between'
                                                style={{ ...inputStyle, padding: '0 12px' }}
                                            >
                                                <span style={{ fontSize: '14px', color: '#2D3748' }}>{selectedArtifact.title}</span>
                                                <button
                                                    type='button'
                                                    className='border-0 bg-transparent'
                                                    style={{ fontSize: '18px', color: '#A0AEC0', cursor: 'pointer' }}
                                                    onClick={() => updateEntry(entry.aarti_id, { aarti_audio_artifact_id: null })}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <Form.Control
                                                    type='text'
                                                    placeholder='Search Audio Artifact...'
                                                    value={search}
                                                    onChange={(e) => setSearchByAartiId((prev) => ({ ...prev, [entry.aarti_id]: e.target.value }))}
                                                    style={{ ...inputStyle, paddingRight: '35px' }}
                                                />
                                                <img
                                                    src={Search}
                                                    alt='search'
                                                    className='position-absolute'
                                                    style={{ right: '10px', top: '12px', width: '16px', height: '16px', pointerEvents: 'none' }}
                                                />
                                                {search.length > 0 && (
                                                    <div
                                                        className='position-absolute w-100 bg-white border rounded mt-1 shadow-sm'
                                                        style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                                                    >
                                                        {filteredResults.map((result) => (
                                                            <div
                                                                key={result.artifact_id}
                                                                className='p-2'
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => {
                                                                    updateEntry(entry.aarti_id, { aarti_audio_artifact_id: result.artifact_id });
                                                                    setSearchByAartiId((prev) => ({ ...prev, [entry.aarti_id]: '' }));
                                                                }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                                                            >
                                                                {result.title}
                                                            </div>
                                                        ))}
                                                        {filteredResults.length === 0 && (
                                                            <div className='p-2 text-muted text-center'>No results</div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </Col>

                            <Col md={3}>
                                <Form.Label className='mb-2' style={labelStyle}>
                                    Aarti Icon Playback Time
                                </Form.Label>
                                <div className='mb-2'>
                                    <Form.Check
                                        inline
                                        type='radio'
                                        name={`icon-playback-mode-${entry.aarti_id}`}
                                        id={`set-time-${entry.aarti_id}`}
                                        label='Set Time'
                                        checked={config.icon_playback_mode === 'SET_TIME'}
                                        onChange={() => updateEntry(entry.aarti_id, { icon_playback_mode: 'SET_TIME' })}
                                    />
                                    <Form.Check
                                        inline
                                        type='radio'
                                        name={`icon-playback-mode-${entry.aarti_id}`}
                                        id={`not-applicable-${entry.aarti_id}`}
                                        label='Not Applicable'
                                        checked={config.icon_playback_mode !== 'SET_TIME'}
                                        onChange={() => updateEntry(entry.aarti_id, {
                                            icon_playback_mode: 'NOT_APPLICABLE',
                                            icon_playback_from: '',
                                            icon_playback_to: '',
                                        })}
                                    />
                                </div>
                                {config.icon_playback_mode === 'SET_TIME' && (
                                    <Row>
                                        <Col xs={6}>
                                            <Form.Label style={{ fontSize: '12px', color: '#718096' }}>From</Form.Label>
                                            <Form.Control
                                                type='time'
                                                value={config.icon_playback_from || ''}
                                                onChange={(e) => updateEntry(entry.aarti_id, { icon_playback_from: e.target.value })}
                                                style={inputStyle}
                                            />
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Label style={{ fontSize: '12px', color: '#718096' }}>To</Form.Label>
                                            <Form.Control
                                                type='time'
                                                value={config.icon_playback_to || ''}
                                                onChange={(e) => updateEntry(entry.aarti_id, { icon_playback_to: e.target.value })}
                                                style={inputStyle}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    </div>
                );
            })}
        </Form.Group>
    );
};

export default AartiSchedulePicker;
